import { useMemo } from "react";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { Table } from "../../table";
import { Group } from "../../group";
import { Icon } from "../../icon";
import { useAutoTableProvider } from "../auto-table-provider";
import { DataTableRowExpansion } from "../auto-table-row-expansion/auto-table-row-expansion";
import { AutoTableRowProvider } from "./auto-table-row-provider/auto-table-row-provider";
import { IProps } from "./auto-table-row.types";
import styles from "../auto-table.module.scss";

/**
 * AutoTableRow component
 * @param index - Index of the row
 * @param row - Data of the row
 * @param rows - All rows data
 * @param children - Child components
 * @param expansion - Row expansion configuration
 * @param columnCount - Total number of columns (used as the expanded cell colSpan)
 * @param rowActions - Renders actions for the row, revealed on row hover or focus
 * @param onClick - Click event handler
 * @returns Row element
 */
const AutoTableRow = <T,>({
    index,
    row,
    children,
    expansion,
    columnCount = 0,
    withChevronColumn,
    rowActions,
    onClick,
    ...otherProps
}: IProps<T>) => {
    const { highlightedRowsKeys, getRowKey, getRowLink } = useAutoTableProvider();
    const expandedElement = expansion && (
        <DataTableRowExpansion
            colSpan={columnCount}
            open={expansion.isRowExpanded(row)}
            content={expansion.content({ record: row, index })}
            collapseProps={expansion.collapseProps}
            rowProps={expansion.expandedRowProps}
            cellProps={expansion.cellProps}
            contentProps={expansion.contentProps}
        />
    );

    const rowLink = useMemo(() => (row !== null ? getRowLink?.(row) : undefined), [getRowLink, row]);

    const isExpanded = useMemo(() => expansion?.isRowExpanded(row) ?? false, [expansion, row]);

    const actions = typeof rowActions === "function" && row !== null ? rowActions(row) : null;

    return (
        <>
            <Table.Tr
                data-highlighted={highlightedRowsKeys?.includes(String(getRowKey(row))) ?? false}
                {...otherProps}
                style={isExpanded ? { ...otherProps.style, borderBottom: "none" } : otherProps.style}
                data-clickable={!!onClick || !!rowLink}
                onClick={(e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
                    if (expansion) {
                        const { isExpandable, isRowExpanded, expandOnClick, expandRow, collapseRow } = expansion;
                        if (isExpandable({ record: row, index }) && expandOnClick) {
                            if (isRowExpanded(row)) {
                                collapseRow(row);
                            } else {
                                expandRow(row);
                            }
                        }
                    }
                    onClick?.(e);
                }}
            >
                {withChevronColumn && (
                    <Table.Td className={styles["chevron-cell"]}>
                        {expansion && row !== null && expansion.isExpandable({ record: row, index }) && (
                            <Icon
                                icon={faChevronRight}
                                size="xs"
                                className={styles["chevron-icon"]}
                                data-expanded={expansion.isRowExpanded(row)}
                            />
                        )}
                    </Table.Td>
                )}
                <AutoTableRowProvider row={row} rowLink={rowLink}>
                    {children}
                </AutoTableRowProvider>
                {typeof rowActions === "function" && (
                    <Table.Td className={styles["row-actions-cell"]} w={0} miw={0} p={0}>
                        {actions !== null && typeof actions !== "undefined" && (
                            // Stop clicks reaching the row so an action doesn't also trigger the row click or expansion.
                            <Group
                                className={styles["row-actions"]}
                                gap="xs"
                                wrap="nowrap"
                                onClick={(event) => event.stopPropagation()}
                                data-testid="auto-table-row-actions"
                            >
                                {actions}
                            </Group>
                        )}
                    </Table.Td>
                )}
            </Table.Tr>
            {expandedElement}
        </>
    );
};

export { AutoTableRow };
